import { useEffect, useRef } from 'react';
import type { ReactElement, RefObject } from 'react';
import { Flex, useBreakpointValue } from '@chakra-ui/react';
import { ErrorBoundary } from 'react-error-boundary';
import { FormattedMessage, useIntl } from 'react-intl';
import { useLocation } from 'react-router';
import { useResizeObserver } from 'usehooks-ts';
import type { PageFC } from '@salesforce/pwa-kit-react-sdk';
import { ProductType } from '../../bff-api/baskets/model/ProductType';
import { ShippingMethodId } from '../../bff-api/baskets/model/ShippingMethodId';
import { useBasket } from '../../bff-api/baskets/useBasket';
import { useBasketAvailability } from '../../bff-api/baskets/useBasketAvailability';
import { useCustomerGet } from '../../bff-api/customers/useCustomerGet';
import { getShippingAddressByMethodId, isBasketEmpty } from '../../bff-api/utils/basket-utils';
import { EnhancedError } from '../../components/_error/EnhancedError';
import { BasketOrderTotalSummary } from '../../components/basket-order-total-summary';
import { ConditionalRender } from '../../components/conditionally-render/ConditionalRender';
import { GetIcon } from '../../components/icons';
import { Layout } from '../../components/layout/Layout';
import ClientRecommendations from '../../components/recommendations/ClientRecommendations';
import { RecommendationLocation } from '../../components/recommendations/RecommendationLocation';
import Seo from '../../components/seo';
import { WidgetRecommendationName } from '../../constants/WidgetRecommendationName';
import { AuthType } from '../../contexts/model/AuthType';
import { useIsMobile } from '../../hooks/use-is-mobile';
import { useCart } from '../../hooks/useCart';
import { useSameDayDelivery } from '../../hooks/useSameDayDelivery';
import { useWidgetRecommendations } from '../../hooks/useWidgetRecommendations';
import { Header, Text } from '../../ui-components';
import { toAddressSectionValue } from '../../utils/address-utils';
import { useCustomPreferences } from '../../utils/api';
import { CustomPreference } from '../../utils/api/CustomPreference';
import { isNoIndexPage } from '../../utils/is-no-index-page';
import { isPhysicalGiftCard } from '../../utils/product-utils';
import { isServer } from '../../utils/utils';
import { configHtmlCache } from '../utils/config-html-cache';
import { CartSection } from './components/CartSection';
import {
    CartItemsSkeleton,
    OrderSummarySkeleton,
    PromoCodeSkeleton,
    TitleSkeleton,
} from './components/CartSkeleton';
import { FreeShippingBarWrapper } from './components/FreeShippingBar';
import { CartButtons } from './components/cart-buttons';
import { CartEmpty } from './components/cart-empty';
import { CartItem } from './components/cart-item/CartItem';
import { EditCulinaryDrawer } from './components/edit-culinary-drawer';
import { CartOrderSummary } from './components/order-summary';
import { CartPromoCode } from './components/promo-code';
import { SaveForLaterWidget } from './components/save-for-later-widget';
import { CartStateProvider, useCartState } from './contexts/useCartState';
import { CulinaryDrawerProvider } from './contexts/useCulinaryDrawer';
import { useScrollToPrevious } from './hooks/useScrollToPrevious';
import type { BloomreachProductType } from '../../bff-api/bloomreach-recommendation/model/bloomreach';

export const CartLayout = (): ReactElement => {
    const intl = useIntl();
    const location = useLocation();
    const {
        isLoading: isAvailabilityLoading,
        isError: isAvailabilityError,
        error: availabilityError,
    } = useBasketAvailability();
    const { isLoading: isBasketLoading, isError: isBasketError, error: basketError } = useBasket();
    const {
        basket,
        isMixedBagWithOneAddress,
        countTotalProductItems,
        lastProductAddedToBasket: product,
    } = useCart();
    const {
        sameDayDeliveryProducts,
        shippingProducts,
        physicalGiftCardProducts,
        bopisProducts,
        culinaryProducts,
        eGiftCardProducts,
        dropShipProducts,
        postPurchaseWarrantyProducts,
        shippingProtectionProducts,
        setHeaderHeight,
        refManager,
    } = useCartState();
    const { data: customer, isLoading: isCustomerGetLoading } = useCustomerGet();
    const { description: sameDayDeliveryTitle } = useSameDayDelivery({
        preferenceForBasketValue: true,
    });

    const { EnableThresoldShipping: isFreeShippingEnabled = false } = useCustomPreferences([
        CustomPreference.ENABLE_THRESHOLD_SHIPPING,
    ]);

    const userId =
        customer?.authType === AuthType.REGISTERED ? customer?.customerNo : customer?.customerId;

    const mapProductTypeToBloomreach = (
        widgetType: WidgetRecommendationName,
        productType?: string,
    ): BloomreachProductType | undefined => {
        // when the widget is cart_item_based_widget and the product type is culinary
        // we want to filter by culinary, any other combination we want to filter by hardgood
        if (
            widgetType === WidgetRecommendationName.CART_FREQUENTLY_BOUGHT_TOGETHER &&
            productType === ProductType.CULINARY
        ) {
            return 'culinary';
        }

        return 'hardgood';
    };

    const recommendationWidgets = useWidgetRecommendations([
        WidgetRecommendationName.CART_RECENTLY_VIEWED,
        WidgetRecommendationName.CART_FREQUENTLY_BOUGHT_TOGETHER,
    ]);

    const isShippingSectionVisible = !!shippingProducts.length || !!dropShipProducts.length;
    const headerRef = refManager('cart-header');
    const { height: headerHeight = 0 } = useResizeObserver({
        ref: headerRef as unknown as RefObject<HTMLElement>,
    });
    const prevHeaderHeight = useRef(headerHeight);

    useScrollToPrevious(headerHeight);

    const isCartLoading = isBasketLoading || (isAvailabilityLoading && !isBasketEmpty(basket));

    const sddAddress = toAddressSectionValue(
        getShippingAddressByMethodId(basket, ShippingMethodId.SAME_DAY_DELIVERY),
    );

    const bopisAddress = toAddressSectionValue(
        getShippingAddressByMethodId(basket, ShippingMethodId.BOPIS),
    );

    const isMobile = useIsMobile();

    useEffect(() => {
        if (isMobile) {
            window.scrollTo(0, 0);
            if (prevHeaderHeight.current !== headerHeight) {
                setHeaderHeight(headerHeight);
            }
        }
    }, [isMobile, setHeaderHeight, headerHeight]);

    if (isBasketError || isAvailabilityError) {
        throw basketError ?? availabilityError;
    }

    const stickyNavigation = useBreakpointValue({ base: false, md: true })!;

    return (
        <Layout
            headerRef={headerRef}
            className="bg-slt-grey-150"
            stickyNavigation={stickyNavigation}
        >
            <Seo
                title={intl.formatMessage({
                    id: 'cart.title',
                    defaultMessage: 'Shopping Bag',
                })}
                noIndex={isNoIndexPage(location?.pathname)}
            />

            <Flex
                data-testid="cart-container"
                columnGap={8}
                rowGap={{ base: 8, md: 16 }}
                direction="column"
                maxWidth={1800}
                marginX="auto"
                padding={{
                    base: '0 0 4rem',
                    sm: '2rem 0 4rem',
                    md: '2rem 2rem 4rem',
                    lg: '2rem 2.5rem 4rem',
                    xl: '4rem',
                }}
            >
                {isCartLoading || !!basket?.productItems?.length ? (
                    <Flex
                        gap={8}
                        background="slt-grey.150"
                        direction={{ base: 'column', md: 'row' }}
                        wrap="wrap"
                        width="100%"
                    >
                        <Flex
                            direction="column"
                            width={{ base: '100%', md: 'calc(60% - 16px)' }}
                            gap={8}
                        >
                            <Flex
                                direction="column"
                                padding={{ base: '1rem', sm: '0 2rem 0', md: '0' }}
                                background={{ base: 'white', sm: 'slt-grey.150' }}
                                width="100%"
                                gap={4}
                                data-testid="card-header"
                            >
                                <TitleSkeleton isLoaded={!isCartLoading}>
                                    <Flex
                                        direction="column"
                                        gap={6}
                                        background={{ base: 'white', sm: 'slt-grey.150' }}
                                    >
                                        <Flex direction="column" gap={2}>
                                            <div className="inline-flex items-center gap-2">
                                                <Header
                                                    variant="header3"
                                                    as="h1"
                                                    style="bold"
                                                    color="slt-pepper.1000"
                                                >
                                                    <FormattedMessage
                                                        id="cart.your.bag"
                                                        defaultMessage="Your Bag"
                                                    />
                                                </Header>
                                                {countTotalProductItems > 0 && (
                                                    <div
                                                        className="text-2xl text-slt-pepper-1000"
                                                        data-testid="cart-items-count"
                                                    >
                                                        <FormattedMessage
                                                            id="cart.items.count"
                                                            defaultMessage="{count, plural, one {(# item)} other {(# items)}}"
                                                            values={{
                                                                count: countTotalProductItems,
                                                            }}
                                                        />
                                                    </div>
                                                )}
                                            </div>

                                            <ConditionalRender
                                                visibility={{ base: true, sm: false }}
                                            >
                                                <BasketOrderTotalSummary showSavings />
                                            </ConditionalRender>
                                        </Flex>

                                        {!isCartLoading && (
                                            <ConditionalRender
                                                visibility={{ base: true, sm: false }}
                                            >
                                                <CartButtons id="cart-header-buttons" />
                                            </ConditionalRender>
                                        )}
                                    </Flex>
                                </TitleSkeleton>
                                <ConditionalRender
                                    visibility={{
                                        base: !!isFreeShippingEnabled,
                                        md: false,
                                    }}
                                >
                                    <FreeShippingBarWrapper
                                        id="progress-bar-mobile"
                                        isCartLoading={isCartLoading}
                                        maxWidth="343px"
                                    />
                                </ConditionalRender>
                                {!isCartLoading && isMixedBagWithOneAddress ? (
                                    <Flex
                                        gap={2}
                                        data-testid="all-items-one-address"
                                        border="1px solid"
                                        borderColor="slt-grey.1000"
                                        align="center"
                                        padding="0.25rem 0.5rem"
                                        borderRadius={4}
                                        background="white"
                                    >
                                        <GetIcon name="info" size="md" />
                                        <Text id="shipping-items-ship-to-one-address-text">
                                            <FormattedMessage
                                                id="shipping_options.ship.to.one.address"
                                                defaultMessage="All items ship to one address."
                                            />
                                        </Text>
                                    </Flex>
                                ) : null}
                            </Flex>
                            <CartItemsSkeleton isLoaded={!isCartLoading}>
                                <Flex direction="column" gap={8}>
                                    {isShippingSectionVisible && (
                                        <CartSection
                                            title={intl.formatMessage({
                                                id: 'cart.section.ship-it.title',
                                                defaultMessage: 'Ship It',
                                            })}
                                            id="ship-to-section"
                                            displayInformationMessage={
                                                shippingProducts.length > dropShipProducts.length
                                            }
                                        >
                                            {[...shippingProducts].map((productItem) =>
                                                !isPhysicalGiftCard(productItem) ? (
                                                    <CartItem
                                                        key={productItem.itemId}
                                                        productItem={productItem}
                                                    />
                                                ) : null,
                                            )}
                                            {shippingProtectionProducts.map((productItem) => (
                                                <CartItem
                                                    key={productItem.itemId}
                                                    productItem={productItem}
                                                />
                                            ))}
                                            {[...physicalGiftCardProducts].map((productItem) => (
                                                <CartItem
                                                    key={productItem.itemId}
                                                    productItem={productItem}
                                                />
                                            ))}
                                        </CartSection>
                                    )}

                                    {!!bopisProducts.length && (
                                        <CartSection
                                            title={intl.formatMessage({
                                                id: 'cart.section.store-pickup.title',
                                                defaultMessage: 'Store Pickup',
                                            })}
                                            id="bopis-section"
                                            address={bopisAddress}
                                        >
                                            {bopisProducts.map((productItem) => (
                                                <CartItem
                                                    key={productItem.itemId}
                                                    productItem={productItem}
                                                />
                                            ))}
                                        </CartSection>
                                    )}

                                    {!!sameDayDeliveryProducts.length && (
                                        <CartSection
                                            title={sameDayDeliveryTitle}
                                            id="sdd-section"
                                            address={sddAddress}
                                        >
                                            {sameDayDeliveryProducts.map((productItem) => (
                                                <CartItem
                                                    key={productItem.itemId}
                                                    productItem={productItem}
                                                />
                                            ))}
                                        </CartSection>
                                    )}

                                    {!!culinaryProducts.length && (
                                        <CartSection
                                            title={intl.formatMessage({
                                                id: 'cart.section.culinary.title',
                                                defaultMessage: 'Cooking classes',
                                            })}
                                            id="culinary-section"
                                        >
                                            <CulinaryDrawerProvider>
                                                {culinaryProducts.map((productItem) => (
                                                    <CartItem
                                                        key={productItem.itemId}
                                                        productItem={productItem}
                                                    />
                                                ))}
                                                <EditCulinaryDrawer />
                                            </CulinaryDrawerProvider>
                                        </CartSection>
                                    )}

                                    {!!eGiftCardProducts?.length && (
                                        <CartSection
                                            title={intl.formatMessage({
                                                id: 'cart.section.e-gift-card.title',
                                                defaultMessage: 'E-Gift Card',
                                            })}
                                            id="e-gift-card-section"
                                        >
                                            {eGiftCardProducts.map((productItem) => (
                                                <CartItem
                                                    key={productItem.itemId}
                                                    productItem={productItem}
                                                />
                                            ))}
                                        </CartSection>
                                    )}

                                    {!!postPurchaseWarrantyProducts.length && (
                                        <CartSection
                                            title={intl.formatMessage({
                                                id: 'cart.section.warranty.title',
                                                defaultMessage: 'Warranty',
                                            })}
                                            id="warranty-section"
                                        >
                                            {postPurchaseWarrantyProducts.map((productItem) => (
                                                <CartItem
                                                    key={productItem.itemId}
                                                    productItem={productItem}
                                                />
                                            ))}
                                        </CartSection>
                                    )}
                                </Flex>
                            </CartItemsSkeleton>
                        </Flex>

                        <Flex width={{ base: '100%', md: 'calc(40% - 16px)' }}>
                            <Flex
                                direction="column"
                                gap={6}
                                // the `+5` is to add a little padding to the top of the buy box to account for the box shadow
                                top={{
                                    base: '0',
                                    // This is safe to be done only if the position is not relative, it is sticky, so it is fine.
                                    md: `${headerHeight + 5}px`,
                                }}
                                position={{
                                    base: 'relative',
                                    md: 'sticky',
                                }}
                                height="fit-content"
                                width="100%"
                            >
                                <ConditionalRender
                                    visibility={{
                                        base: false,
                                        md: !!isFreeShippingEnabled,
                                    }}
                                >
                                    <FreeShippingBarWrapper
                                        id="progress-bar-desktop"
                                        isCartLoading={isCartLoading}
                                    />
                                </ConditionalRender>

                                <OrderSummarySkeleton
                                    isLoaded={!isCartLoading}
                                    padding={{ base: '1rem', sm: '1rem 2rem', md: '1rem' }}
                                    background="white"
                                >
                                    {!isCartLoading && <CartOrderSummary />}
                                </OrderSummarySkeleton>

                                <PromoCodeSkeleton
                                    isLoaded={!isCartLoading}
                                    padding={{ base: '1rem', sm: '1rem 2rem', md: '1rem' }}
                                    background="white"
                                >
                                    <CartPromoCode />
                                </PromoCodeSkeleton>
                            </Flex>
                        </Flex>
                    </Flex>
                ) : (
                    <CartEmpty />
                )}
                <SaveForLaterWidget />
            </Flex>

            {!isCustomerGetLoading && recommendationWidgets?.length ? (
                <div className="overflow-hidden bg-white">
                    <div className="mx-auto flex w-full max-w-[1800px] flex-col gap-8  sm:px-[2rem] lg:px-[2.5rem] xl:px-16">
                        {recommendationWidgets.map((widget) => (
                            <ClientRecommendations
                                key={widget.id}
                                widget={widget}
                                location={RecommendationLocation.SHOPPING_BAG}
                                productId={product?.master?.masterId}
                                categoryId={product?.c_primaryCategory?.id}
                                brandCategoryId={product?.c_brandCategoryId}
                                collectionId={product?.c_collectionId}
                                userId={userId}
                                productTypeFilter={mapProductTypeToBloomreach(
                                    widget.name as WidgetRecommendationName,
                                    product?.c_productType,
                                )}
                            />
                        ))}
                    </div>
                </div>
            ) : null}
        </Layout>
    );
};

const Cart: PageFC = () => (
    <ErrorBoundary FallbackComponent={EnhancedError}>
        <CartStateProvider>
            <CartLayout />
        </CartStateProvider>
    </ErrorBoundary>
);

Cart.defaultProps = {};

Cart.shouldGetProps = (): Promise<boolean> => Promise.resolve(isServer);

Cart.getProps = ({ res }): Promise<object> => {
    configHtmlCache(res);
    return Promise.resolve({});
};

export { Cart };
